/**
 * The measure session, as the editor sees it — `docs/property-maps-v2.md`
 * §5.4.
 *
 * `measure-session.ts` is the store; this is the behaviour on top of it, so
 * the editor renders and does not also have to know what measuring means.
 *
 * **A shape drawn here never reaches the map endpoints.** It goes on the
 * tape, where it stays until somebody presses *Add to property measurements*
 * — which writes a **part**, not a shape, and leaves the outline where it is.
 * That is the whole of why this is separate from `useMapEditing`: that one
 * saves everything immediately, and here saving is a decision.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { unitFamily } from '@rowted/shared'
import type { Draft } from '../draw/index.js'
import { measurementTypes, type MeasurementType } from '../api.js'
import {
  unsavedCount, useMeasureSession, type MeasureShape,
} from '../measure-session.js'
import { valueOf } from './rows.js'
import type { SaveRow } from './save-dialog.js'

/** What the last area and the last line were filed under, per browser. */
const LAST_TYPE = (family: string) => `rowted.measure.type.${family}`

function rememberType(family: string, typeId: string): void {
  try { window.localStorage.setItem(LAST_TYPE(family), typeId) } catch { /* private */ }
}

function lastType(family: string): string | null {
  try { return window.localStorage.getItem(LAST_TYPE(family)) } catch { return null }
}

export function useMeasure(propertyId: string, on: boolean) {
  const store = useMeasureSession()
  const [types, setTypes] = useState<MeasurementType[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)

  useEffect(() => { store.hydrate(propertyId) }, [propertyId, store])

  /*
   * The types are the Type picker *and* the unit every number is read in, so
   * they are fetched whenever the tab is open rather than only in a session:
   * a saved part's group header needs the unit too.
   */
  useEffect(() => {
    let live = true
    measurementTypes(propertyId)
      .then((body) => { if (live) setTypes(body.items) })
      .catch(() => { if (live) setTypes([]) })
    return () => { live = false }
  }, [propertyId])

  const tape = useMemo(
    () => store.byProperty[propertyId] ?? [], [store.byProperty, propertyId])
  const selected = tape.find((one) => one.id === selectedId) ?? null
  const unsaved = unsavedCount(tape)

  /**
   * A finished outline joins the tape, pre-named and pre-filed.
   *
   * §5.4 asks for `Measurement 1` and "the last type used for the same shape
   * family" — both so that the common case is *draw, press Add* rather than
   * *draw, name, choose, press Add*, which is the difference between
   * measuring a site in five minutes and in twenty.
   */
  const add = useCallback((made: Draft): MeasureShape | null => {
    if (!on) return null
    const family = made.kind === 'line' ? 'length' : 'area'
    const fits = types.filter((one) => unitFamily(one.unit) === family)
    const remembered = lastType(family)
    const typeId = fits.find((one) => one.measurementTypeId === remembered)?.measurementTypeId
      ?? fits[0]?.measurementTypeId
      ?? null

    const shape = store.add(propertyId, {
      kind: made.kind,
      geometry: made.geometry,
      shape: made.shape,
      name: `Measurement ${tape.length + 1}`,
      typeId,
    })
    setSelectedId(shape.id)
    return shape
  }, [on, types, store, propertyId, tape.length])

  const change = useCallback((id: string, patch: Partial<MeasureShape>) => {
    if (patch.typeId) {
      const type = types.find((one) => one.measurementTypeId === patch.typeId)
      if (type) rememberType(unitFamily(type.unit), patch.typeId)
    }
    store.patch(propertyId, id, patch)
  }, [store, propertyId, types])

  const discard = useCallback((id: string) => {
    setSelectedId((was) => (was === id ? null : was))
    store.remove(propertyId, id)
  }, [store, propertyId])

  /** What the review dialog is about to write, for one shape or for all. */
  const rowsFor = useCallback((only?: string): SaveRow[] => tape
    .filter((shape) => !shape.saved && shape.typeId
      && (only === undefined || shape.id === only))
    .flatMap((shape) => {
      const type = types.find((one) => one.measurementTypeId === shape.typeId)
      if (!type) return []
      return [{
        shapeId: shape.id,
        name: shape.name.trim() || 'Measurement',
        typeId: type.measurementTypeId,
        typeName: type.name,
        unit: type.unit,
        value: valueOf(shape, type.unit),
        source: 'aerial' as const,
      }]
    }), [tape, types])

  /** After the dialog: the shapes that are now parts stop being unsaved. */
  const markSaved = useCallback((rows: SaveRow[]) => {
    store.markSaved(propertyId,
      rows.map((row) => row.shapeId).filter((one): one is string => Boolean(one)))
  }, [store, propertyId])

  const clear = useCallback(() => {
    setSelectedId(null)
    store.clear(propertyId)
  }, [store, propertyId])

  return {
    types, tape, unsaved, selected, selectedId, setSelectedId,
    add, change, discard, rowsFor, markSaved, clear,
  }
}
